import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useCohorts } from "@/lib/api/students";
import { useBulkCreateAssessment, usePromptTemplates, useCreatePromptTemplate, type AssessmentMode, type PromptTemplate } from "@/lib/api/assessments";
import { getErrorMessage } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Clock, 
  Users, 
  FileText, 
  CalendarDays,
  Sparkles
} from "lucide-react";

const assessmentModes: { id: AssessmentMode; title: string; description: string; example: string }[] = [
  {
    id: "presenting",
    title: "Presenting",
    description: "Students present information to an audience",
    example: "Tell us about your favorite hobby and why you enjoy it",
  },
  {
    id: "explaining",
    title: "Explaining",
    description: "Students explain how something works or how to do something",
    example: "Explain how to make a paper airplane",
  },
  {
    id: "persuading",
    title: "Persuading",
    description: "Students persuade others to take action or believe something",
    example: "Tell us whether you think schools should have longer lunch breaks",
  },
];

const timeLimits = [
  { value: 60, label: "1 minute" },
  { value: 120, label: "2 minutes" },
  { value: 180, label: "3 minutes" },
  { value: 300, label: "5 minutes" },
  { value: 600, label: "10 minutes" },
];

const yearGroupToAgeBand: Record<string, string> = {
  "4": "8-9",
  "5": "9-10",
  "6": "10-11",
  "7": "11-12",
  "8": "12-13",
  "9": "13-14",
  "10": "14-15",
  "11": "15-16",
  "12": "16-17",
  "13": "17-18",
};

const yearGroupLabels: Record<string, string> = {
  "3": "Year 3 (Age 7-8)",
  "4": "Year 4 (Age 8-9)",
  "5": "Year 5 (Age 9-10)",
  "6": "Year 6 (Age 10-11)",
  "7": "Year 7 (Age 11-12)",
  "8": "Year 8 (Age 12-13)",
  "9": "Year 9 (Age 13-14)",
  "10": "Year 10 (Age 14-15)",
  "11": "Year 11 (Age 15-16)",
  "12": "Year 12 (Age 16-17)",
  "13": "Year 13 (Age 17-18)",
  "Band 1": "Band 1 (Ages 8-11)",
  "Band 2": "Band 2 (Ages 12-14)",
  "Band 3": "Band 3 (Ages 15-18)",
};

export default function CreateAssessmentPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Data
  const { data: cohorts, isLoading: cohortsLoading } = useCohorts();
  const { data: promptTemplates, isLoading: templatesLoading, refetch: refetchTemplates } = usePromptTemplates();
  const bulkCreateAssessment = useBulkCreateAssessment();
  const createPromptTemplate = useCreatePromptTemplate();
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [mode, setMode] = useState<AssessmentMode>("presenting" as AssessmentMode);
  const [selectedCohortIds, setSelectedCohortIds] = useState<string[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string>("");
  const [customTopic, setCustomTopic] = useState("");
  const [timeLimit, setTimeLimit] = useState(180);
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>(undefined);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);

  // Get selected cohort(s) to derive age band
  const selectedCohorts = cohorts?.filter(c => selectedCohortIds.includes(c.id)) || [];
  const derivedAgeBand = selectedCohorts.length > 0 
    ? selectedCohorts[0].age_band || yearGroupToAgeBand[selectedCohorts[0].year_group] || ""
    : "";

  // Filter prompts by mode and age band (derived from selected cohort)
  const filteredPrompts = promptTemplates?.filter(
    (p) => p.mode === mode && p.age_band === derivedAgeBand
  ) || [];

  // Create prompt state
  const [showCreatePrompt, setShowCreatePrompt] = useState(false);
  const [newPromptName, setNewPromptName] = useState("");
  const [newPromptTemplate, setNewPromptTemplate] = useState("");
  const [newPromptRequiresTopic, setNewPromptRequiresTopic] = useState(false);

  const handleCreatePrompt = async () => {
    if (!newPromptName || !newPromptTemplate) return;

    try {
      const newPrompt = await createPromptTemplate.mutateAsync({
        name: newPromptName,
        mode: mode,
        age_band: derivedAgeBand,
        instructions: newPromptTemplate,
        time_limit_seconds: timeLimit,
        requires_topic: newPromptRequiresTopic,
        is_active: true,
        is_default: false,
      });

      setSelectedPromptId(newPrompt.id);
      setShowCreatePrompt(false);
      setNewPromptName("");
      setNewPromptTemplate("");
      setNewPromptRequiresTopic(false);

      toast({
        title: "Template Created",
        description: "Your instruction has been created successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create instruction",
        variant: "destructive",
      });
    }
  };
  
  const steps = [
    { title: "Mode", description: "Select assessment type", icon: FileText },
    { title: "Cohorts", description: "Select students", icon: Users },
    { title: "Instruction", description: "Choose or create instruction", icon: Sparkles },
    { title: "Schedule", description: "Set timing", icon: CalendarDays },
    { title: "Review", description: "Confirm details", icon: Check },
  ];
  
  // Check if all selected cohorts have the same year group
  const allSameYearGroup = selectedCohorts.length <= 1 || 
    selectedCohorts.every(c => c.year_group === selectedCohorts[0].year_group);

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return !!mode;
      case 1:
        return selectedCohortIds.length > 0 && allSameYearGroup && !!derivedAgeBand;
      case 2:
        return !!derivedAgeBand && !!timeLimit && (!!selectedPromptId || showCreatePrompt);
      case 3:
        return true; // Schedule is optional
      case 4:
        return true;
      default:
        return false;
    }
  };
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleSubmit = async () => {
    // Bulk create assessments for all selected cohorts
    try {
      let totalCreated = 0;
      let allSkipped = true;
      
      for (const cohortId of selectedCohortIds) {
        const result = await bulkCreateAssessment.mutateAsync({
          cohort_id: cohortId,
          mode: mode,
          prompt_id: selectedPromptId || undefined,
          time_limit_seconds: timeLimit,
        });
        
        totalCreated += result.count;
        if (result.count > 0) {
          allSkipped = false;
        }
      }
      
      if (allSkipped) {
        toast({
          title: "Assessments Already Exist",
          description: "All students in the selected cohort(s) already have assessments for this mode.",
        });
      } else {
        toast({
          title: "Assessments Created",
          description: `Successfully created ${totalCreated} assessment(s)`,
        });
      }
      
      navigate("/teacher");
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const toggleCohort = (cohortId: string) => {
    setSelectedCohortIds(prev => {
      const newSelection = prev.includes(cohortId)
        ? prev.filter(id => id !== cohortId)
        : [...prev, cohortId];
      // Clear selected prompt when cohorts change since prompts are age-band specific
      if (newSelection.length !== prev.length) {
        setSelectedPromptId("");
      }
      return newSelection;
    });
  };
  
  return (
    <div className="container max-w-4xl mx-auto py-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Create Assessment</h1>
        <p className="text-muted-foreground">
          Set up a new oracy assessment for your students
        </p>
      </div>
      
      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={index} className="flex items-center">
            <div
              className={`flex shrink-0 items-center justify-center w-10 h-10 rounded-full border-2 ${
                index < currentStep
                  ? "bg-primary border-primary text-primary-foreground"
                  : index === currentStep
                  ? "border-primary text-primary"
                  : "border-muted-foreground text-muted-foreground"
              }`}
            >
              {index < currentStep ? (
                <Check className="w-5 h-5" />
              ) : (
                <step.icon className="w-5 h-5" />
              )}
            </div>
            {index < steps.length - 1 && (
              <div
                className={`w-6 sm:w-16 md:w-24 h-0.5 mx-1 sm:mx-2 ${
                  index < currentStep ? "bg-primary" : "bg-muted"
                }`}
              />
            )}
          </div>
        ))}
      </div>
      
      {/* Step Content */}
      <Card>
        <CardHeader>
          <CardTitle>{steps[currentStep].title}</CardTitle>
          <CardDescription>{steps[currentStep].description}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Step 1: Mode Selection */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <RadioGroup
                value={mode}
                onValueChange={(v) => setMode(v as AssessmentMode)}
                className="grid gap-4 md:grid-cols-3"
              >
                {assessmentModes.map((m) => (
                  <Label
                    key={m.id}
                    className={`cursor-pointer border-2 rounded-lg p-4 hover:border-primary transition-colors ${
                      mode === m.id ? "border-primary bg-primary/5" : ""
                    }`}
                  >
                    <RadioGroupItem value={m.id} className="sr-only" />
                    <div className="font-medium">{m.title}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {m.description}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2 italic">
                      Example: "{m.example}"
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          )}
          
          {/* Step 2: Cohorts */}
          {currentStep === 1 && (
            <div className="space-y-4">
              {!allSameYearGroup && selectedCohortIds.length > 1 && (
                <div className="p-3 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                  Selected cohorts have different year groups. Please select cohorts from the same year group.
                </div>
              )}
              {cohortsLoading ? (
                <div className="text-center py-8">Loading cohorts...</div>
              ) : cohorts && cohorts.length > 0 ? (
                <div className="space-y-2">
                  {cohorts.map((cohort) => (
                    <div
                      key={cohort.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 ${
                        selectedCohortIds.includes(cohort.id) ? "border-primary bg-primary/5" : ""
                      }`}
                      onClick={() => toggleCohort(cohort.id)}
                    >
                      <Checkbox
                        checked={selectedCohortIds.includes(cohort.id)}
                        onCheckedChange={() => toggleCohort(cohort.id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{cohort.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {yearGroupLabels[cohort.year_group] || `Year ${cohort.year_group}`} · {cohort.academic_year}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No cohorts found</p>
                  <p className="text-sm">Create a cohort first to assign assessments</p>
                </div>
              )}
              
              {selectedCohortIds.length > 0 && (
                <div className="mt-4">
                  <Badge variant="secondary">
                    {selectedCohortIds.length} cohort(s) selected
                  </Badge>
                  {derivedAgeBand && (
                    <Badge variant="outline" className="ml-2">
                      Age band: {derivedAgeBand}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Template */}
          {currentStep === 2 && derivedAgeBand && (
            <div className="space-y-6">
              <div className="p-3 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground">Age Band: </span>
                <span className="text-sm font-medium">{derivedAgeBand}</span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Instruction</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCreatePrompt(true)}
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Create New
                  </Button>
                </div>
                {templatesLoading ? (
                  <div className="text-sm text-muted-foreground">Loading instructions...</div>
                ) : filteredPrompts.length > 0 ? (
                  <RadioGroup
                    value={selectedPromptId}
                    onValueChange={setSelectedPromptId}
                    className="grid gap-3 md:grid-cols-2"
                  >
                    {filteredPrompts.map((prompt) => (
                      <Label
                        key={prompt.id}
                        className={`cursor-pointer border-2 rounded-lg p-4 hover:border-primary transition-colors ${
                          selectedPromptId === prompt.id ? "border-primary bg-primary/5" : ""
                        }`}
                      >
                        <RadioGroupItem value={prompt.id} className="sr-only" />
                        <div className="font-medium">{prompt.name}</div>
                        {prompt.requires_topic && (
                          <div className="text-xs text-amber-600 mt-2">
                            Requires custom topic
                          </div>
                        )}
                      </Label>
                    ))}
                  </RadioGroup>
                ) : (
                    <div className="text-sm text-muted-foreground">
                      No instructions available. Create a new one.
                    </div>
                )}
              </div>

              {(selectedPromptId && filteredPrompts.find(p => p.id === selectedPromptId)?.requires_topic) && (
                <div className="space-y-2">
                  <Label>Custom Topic</Label>
                  <Textarea
                    placeholder="Enter a custom topic..."
                    value={customTopic}
                    onChange={(e) => setCustomTopic(e.target.value)}
                    rows={3}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Time Limit</Label>
                <Select value={timeLimit.toString()} onValueChange={(v) => setTimeLimit(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeLimits.map((limit) => (
                      <SelectItem key={limit.value} value={limit.value.toString()}>
                        {limit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Create Prompt Dialog */}
              {showCreatePrompt && (
                <div className="border rounded-lg p-4 space-y-4 mt-4">
                  <h4 className="font-medium">Create New Instruction</h4>
                  <div className="space-y-2">
                    <Label>Instruction Name</Label>
                    <Input
                      placeholder="e.g., Climate Change Presentation"
                      value={newPromptName}
                      onChange={(e) => setNewPromptName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Instructions</Label>
                    <Textarea
                      placeholder="Instructions for the student (e.g., 'Tell us whether you think schools should have longer lunch breaks, and why.')"
                      value={newPromptTemplate}
                      onChange={(e) => setNewPromptTemplate(e.target.value)}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      Write clear instructions that students will see before recording
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="requiresTopic"
                      checked={newPromptRequiresTopic}
                      onCheckedChange={(checked) => setNewPromptRequiresTopic(checked === true)}
                    />
                    <Label htmlFor="requiresTopic" className="cursor-pointer">
                      Let me specify a custom topic for students
                    </Label>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowCreatePrompt(false);
                        setNewPromptName("");
                        setNewPromptTemplate("");
                        setNewPromptRequiresTopic(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleCreatePrompt}
                      disabled={!newPromptName || !newPromptTemplate || createPromptTemplate.isPending}
                    >
                      {createPromptTemplate.isPending ? "Creating..." : "Create Instruction"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!derivedAgeBand && currentStep === 2 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>Please select a cohort first to determine the age band.</p>
            </div>
          )}
          
          {/* Step 4: Schedule */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="schedule"
                  checked={scheduleEnabled}
                  onCheckedChange={(checked) => setScheduleEnabled(checked === true)}
                />
                <Label htmlFor="schedule" className="cursor-pointer">
                  Schedule for later
                </Label>
              </div>
              
              {scheduleEnabled && (
                <div className="space-y-2">
                  <Label>Assessment Date & Time</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <Clock className="mr-2 h-4 w-4" />
                        {scheduleDate ? (
                          scheduleDate.toLocaleString()
                        ) : (
                          "Pick a date and time"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={scheduleDate}
                        onSelect={setScheduleDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                    Students will see the assessment at the scheduled time
                  </p>
                </div>
              )}
              
              {!scheduleEnabled && (
                <div className="text-sm text-muted-foreground">
                  Assessments will be available immediately after creation
                </div>
              )}
            </div>
          )}
          
          {/* Step 5: Review */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Assessment Mode</div>
                  <div className="font-medium capitalize">{mode}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Age Band</div>
                  <div className="font-medium">{derivedAgeBand}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Instruction</div>
                  <div className="font-medium">
                    {selectedPromptId 
                      ? filteredPrompts.find((p) => p.id === selectedPromptId)?.name || newPromptName || "Custom"
                      : "Custom"}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Time Limit</div>
                  <div className="font-medium">
                    {timeLimits.find((tl) => tl.value === timeLimit)?.label}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Cohorts</div>
                  <div className="font-medium">
                    {selectedCohortIds.length} cohort(s)
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Scheduled</div>
                  <div className="font-medium">
                    {scheduleEnabled && scheduleDate
                      ? scheduleDate.toLocaleString()
                      : "Immediately"}
                  </div>
                </div>
                {customTopic && (
                  <div className="space-y-1 md:col-span-2">
                    <div className="text-sm text-muted-foreground">Custom Topic</div>
                    <div className="font-medium">{customTopic}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
        
        {/* Navigation */}
        <div className="flex justify-between p-6 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          {currentStep < steps.length - 1 ? (
            <Button onClick={handleNext} disabled={!canProceed()}>
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={bulkCreateAssessment.isPending}
            >
              {bulkCreateAssessment.isPending ? "Creating..." : "Create Assessment"}
              <Check className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
